/**
 * Site wrapper for nara-orbe-voice26.html.
 * Phases: idle | listening | thinking | processing | speaking
 * Moods: DEFAULT | STRICT | ENERGY | CALM | HEALTH | MINDSET | SUCCESS | MEDITATION
 */
const PHASES = ['idle', 'listening', 'thinking', 'processing', 'speaking'];
const MOODS = ['DEFAULT', 'STRICT', 'ENERGY', 'CALM', 'HEALTH', 'MINDSET', 'SUCCESS', 'MEDITATION'];
const HOME_MOOD = 'DEFAULT';
const HOME_CYCLE_MOODS = Object.freeze([
  'CALM',
  'ENERGY',
  'SUCCESS',
  'STRICT',
  'MINDSET',
]);
const MOOD_RGB = {
  DEFAULT: [211, 149, 66],
  STRICT: [139, 46, 46],
  ENERGY: [224, 122, 47],
  CALM: [142, 196, 212],
  HEALTH: [79, 143, 91],
  MINDSET: [176, 122, 168],
  SUCCESS: [232, 193, 90],
  MEDITATION: [184, 164, 212],
};
const IMPULSE_MS = 320;
const PHASE_ALIAS = {
  loading: 'idle',
  anneaux: 'listening',
  idle: 'idle',
  listening: 'listening',
  thinking: 'thinking',
  processing: 'processing',
  speaking: 'speaking',
};

function resolvePhase(value) {
  return PHASE_ALIAS[String(value || 'idle').toLowerCase()] || 'idle';
}

function resolveMood(value) {
  const key = String(value || 'DEFAULT').trim();
  const upper = key.toUpperCase();
  if (MOODS.includes(upper)) return upper;
  const fromMood = key.replace(/^mood_/i, '').toUpperCase();
  return MOODS.includes(fromMood) ? fromMood : 'DEFAULT';
}

class NaraVoiceOrb extends HTMLElement {
  connectedCallback() {
    if (this.booted) return;
    this.booted = true;
    this.voiceReady = false;
    this.pending = { state: resolvePhase(this.getAttribute('data-state')), mood: resolveMood(this.getAttribute('data-mood')) };
    this.cycleTimer = 0;
    this.frame = this.querySelector('iframe');
    if (!this.frame) {
      this.frame = document.createElement('iframe');
      this.frame.setAttribute('title', this.getAttribute('data-title') || 'Nara orb');
      this.frame.setAttribute('tabindex', '-1');
      this.frame.setAttribute('aria-hidden', 'true');
      this.append(this.frame);
    }
    this.frame.addEventListener('load', () => {
      this.voiceReady = true;
      this.sync();
    });
    this.boot();
    this.observer = new MutationObserver(() => {
      this.pending.state = resolvePhase(this.getAttribute('data-state'));
      this.pending.mood = resolveMood(this.getAttribute('data-mood'));
      this.sync();
      this.syncCycle();
    });
    this.observer.observe(this, { attributes: true, attributeFilter: ['data-state', 'data-mood', 'data-cycle', 'data-cycle-ms'] });
    this.syncCycle();
    this.bindMoodTap();
  }

  disconnectedCallback() {
    this.clearCycle();
    this.observer?.disconnect();
    this.moodAbort?.abort();
  }

  async boot() {
    const src = this.frame.getAttribute('src') || this.getAttribute('data-src');
    if (!src) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    try {
      const html = await fetch(src).then((response) => response.text());
      if (!html || !html.includes('nara-orbe-voice26')) return;
      this.frame.removeAttribute('src');
      this.frame.srcdoc = html;
    } catch {
      if (!this.frame.getAttribute('src')) this.frame.src = src;
    }
  }

  setState(state) {
    this.setAttribute('data-state', resolvePhase(state));
  }

  setMood(mood) {
    this.setAttribute('data-mood', resolveMood(mood));
  }

  post(payload) {
    if (!this.frame?.contentWindow || !this.voiceReady) return false;
    this.frame.contentWindow.postMessage(payload, '*');
    return true;
  }

  sync() {
    const { state, mood } = this.pending;
    if (!this.post({ type: 'nara-orb-state', state, mood })) return;
    this.post({ type: 'nara-orb-mood', mood });
  }

  syncCycle() {
    this.clearCycle();
    const mode = String(this.getAttribute('data-cycle') || '').toLowerCase();
    if (!mode || mode === 'off' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ms = Math.max(2400, Number(this.getAttribute('data-cycle-ms') || 5600) || 5600);
    let phaseIndex = Math.max(0, PHASES.indexOf(resolvePhase(this.getAttribute('data-state'))));
    let moodIndex = Math.max(0, MOODS.indexOf(resolveMood(this.getAttribute('data-mood'))));
    this.cycleTimer = window.setInterval(() => {
      if (mode === 'phases' || mode === 'both') {
        phaseIndex = (phaseIndex + 1) % PHASES.length;
        this.setAttribute('data-state', PHASES[phaseIndex]);
      }
      if (mode === 'moods' || mode === 'both') {
        moodIndex = (moodIndex + 1) % MOODS.length;
        this.setAttribute('data-mood', MOODS[moodIndex]);
      }
    }, ms);
  }

  clearCycle() {
    if (this.cycleTimer) window.clearInterval(this.cycleTimer);
    this.cycleTimer = 0;
  }

  bindMoodTap() {
    if (this.getAttribute('data-mood-tap') !== 'home-cycle') return;
    this.atHome = true;
    this.guestIndex = 0;
    this.lastTapAt = 0;
    this.moodAbort = new AbortController();
    const { signal } = this.moodAbort;
    let startX = 0;
    let startY = 0;
    this.syncHalo(HOME_MOOD);

    this.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
      },
      { signal }
    );
    this.addEventListener(
      'pointerup',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (Math.hypot(dx, dy) > 12) return;
        this.cycleMoodTap();
      },
      { signal }
    );
    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.cycleMoodTap();
      },
      { signal }
    );
  }

  cycleMoodTap() {
    const now = performance.now();
    if (now - this.lastTapAt < 280) return;
    this.lastTapAt = now;
    const guests = window.NARA_ORB_MOODS?.NARA_HOME_CYCLE_MOODS || HOME_CYCLE_MOODS;
    if (!this.atHome) {
      this.atHome = true;
      this.setMood(HOME_MOOD);
      this.setState('idle');
      this.syncHalo(HOME_MOOD);
      this.bounce();
      return;
    }
    const next = guests[this.guestIndex % guests.length] || 'CALM';
    this.guestIndex = (this.guestIndex + 1) % guests.length;
    this.atHome = false;
    this.setMood(next);
    this.setState('speaking');
    this.syncHalo(next);
    this.bounce();
  }

  syncHalo(moodId) {
    const host = this.closest('.nara-landing-meaning__orb-col');
    const rgb = MOOD_RGB[resolveMood(moodId)] || MOOD_RGB.DEFAULT;
    if (!host) return;
    host.style.setProperty('--nara-meaning-glow', `${rgb[0]} ${rgb[1]} ${rgb[2]}`);
    host.style.setProperty('--nara-meaning-glow-soft', `${rgb[0]} ${rgb[1]} ${rgb[2]}`);
  }

  bounce() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.classList.remove('is-bounce');
    void this.offsetWidth;
    this.classList.add('is-bounce');
    window.setTimeout(() => this.classList.remove('is-bounce'), IMPULSE_MS + 40);
  }
}

if (!customElements.get('nara-voice-orb')) {
  customElements.define('nara-voice-orb', NaraVoiceOrb);
}
